import { useUser } from "@/context/useUser";
import {
  DynamicModuleService,
  SchemaService,
  UserPreferencesService,
  PtsEmployeeService,
  PtsProjectService,
  PtsTaskListingService,
  TenantRoleService,
} from "@/services/APIs";
import { gridName } from "@/utils/constant/gridName";
import { ConfigDynamicTableProps } from "@/utils/types/baseTable";
import { ProTable } from "@ant-design/pro-components";
import { useQuery } from "@tanstack/react-query";
import { Table } from "antd";
import { useEffect, useMemo, useState, useCallback } from "react";

const ConfigDynamicTable = <T extends object>({
  columns,
  headerTitle,
  rowKey,
  filter,
  toolBarRender,
  selectedRowKeys,
  setSelectedRowKeys,
  tableName,
  expandable,
}: ConfigDynamicTableProps<T> & {
  tableName: string;
}) => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(200);

  const { data: columnGridData } = useQuery({
    queryKey: ["columnGridData", tableName],
    queryFn: () => SchemaService.getColumnGridData(tableName ?? ""),
    enabled: !columns.length || columns.length === 0,
  });

  // Use useMemo to compute dynamic columns
  const finalColumns = useMemo(() => {
    if (columns.length > 0) {
      return columns;
    }

    return (
      columnGridData?.grid?.map((field: any) => ({
        title: field.field_title,
        dataIndex: field.field_name.toLowerCase(),
      })) ?? []
    );
  }, [columns, columnGridData]);

  const {
    data: moduleDataResponse,
    isLoading: moduleLoading,
    refetch,
  } = useQuery({
    queryKey: ["moduleData", tableName, page, pageSize, finalColumns, filter],
    queryFn: () => {
      // Use PtsEmployeeService for PTS Emp to call base endpoint without grid_data
      if (tableName === "PTS Emp") {
        return PtsEmployeeService.getEmployees(filter, page, pageSize);
      }
      // Use PtsProjectService for PTS Project to call base endpoint without grid_data
      if (tableName === "PTS Project") {
        return PtsProjectService.getProjects(filter, page, pageSize);
      }
      // Use getItemsByPage for PTS Sprint, PTS Task, and PTS Leave Application to use base endpoint without /grid_data/ and with page/page_size
      if (
        tableName === "PTS Sprint" ||
        tableName === "PTS Task" ||
        tableName === "PTS Leave Application" ||
        tableName === "PTS Task Listing"
      ) {
        const orderingParams: Record<string, any> = { ...filter };
        if (tableName === "PTS Sprint") {
          orderingParams.ordering = "sprint_name";
        } else if (tableName === "PTS Task") {
          orderingParams.ordering = '["project.projectname", "taskname"]';
          orderingParams.is_pts_tsk = true;
        } else if (tableName === "PTS Task Listing") {
          return PtsTaskListingService.getTaskListing({
            page,
            page_size: pageSize,
            ...orderingParams,
          });
        }
        return DynamicModuleService.getItemsByPage(
          tableName ?? "",
          page,
          pageSize,
          orderingParams
        );
      }
      // Use getGridColItems for other PTS models to use /grid_data/ endpoint with page/page_size
      if (tableName === "PTS Attendance" || tableName === "PTS Employee Rate") {
        const orderingParams: Record<string, any> = { ...filter };
        if (tableName === "PTS Attendance") {
          orderingParams.ordering = '["-date", "empname.firstname"]';
        }
        return DynamicModuleService.getGridColItems(
          tableName ?? "",
          page,
          pageSize,
          orderingParams
        );
      }
      // Use TenantRoleService for Roles
      if (tableName === "Roles") {
        return TenantRoleService.listRoles(page, pageSize);
      }
      // Use getGridCoByConfig for other tables
      return DynamicModuleService.getGridCoByConfig(
        tableName ?? "",
        page,
        pageSize,
        [
          "id", // Always include id field
          ...finalColumns
            .filter(
              (column: any) => column.dataIndex && column.dataIndex !== "id"
            )
            .map((column: any) => column.dataIndex),
        ],
        {
          ...filter,
        }
      );
    },
    enabled: !!tableName && (tableName === "Roles" || finalColumns.length > 0),
  });

  // Transform data to tree structure for PTS Task (convert subtask array to children)
  const transformToTreeData = useCallback((data: any[]): any[] => {
    return data.map((item: any) => {
      const transformed: any = { ...item };
      // Convert subtask array to children for Ant Design Table tree structure
      if (
        item.subtask &&
        Array.isArray(item.subtask) &&
        item.subtask.length > 0
      ) {
        transformed.children = transformToTreeData(item.subtask);
      }
      return transformed;
    });
  }, []);

  // Extract data array from response (handle both array and object with results property)
  const moduleData = useMemo(() => {
    if (!moduleDataResponse) {
      return [];
    }
    let data: any[] = [];
    if (Array.isArray(moduleDataResponse)) {
      data = moduleDataResponse;
    } else if (
      moduleDataResponse.results &&
      Array.isArray(moduleDataResponse.results)
    ) {
      data = moduleDataResponse.results;
    }

    // Transform to tree structure for PTS Task and PTS Task Listing
    if (
      (tableName === "PTS Task" || tableName === "PTS Task Listing") &&
      data.length > 0
    ) {
      return transformToTreeData(data);
    }

    return data;
  }, [moduleDataResponse, tableName, transformToTreeData]);

  // Tree tables that should always show their subtasks expanded
  const isTreeTable = tableName === "PTS Task Listing";

  const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);

  // Collect keys of every row that has children so they can be expanded by default
  const collectExpandableKeys = useCallback(
    (data: any[]): React.Key[] => {
      const keys: React.Key[] = [];
      const walk = (items: any[]) => {
        items.forEach(item => {
          if (Array.isArray(item.children) && item.children.length > 0) {
            const key =
              typeof rowKey === "string"
                ? item[rowKey]
                : (item.id ?? item.ID ?? item.Id ?? item._id);
            if (key !== undefined && key !== null) {
              keys.push(key);
            }
            walk(item.children);
          }
        });
      };
      walk(data);
      return keys;
    },
    [rowKey]
  );

  // Re-expand all parent rows whenever the data changes (filter/search/pagination/refetch)
  useEffect(() => {
    if (isTreeTable) {
      setExpandedRowKeys(collectExpandableKeys(moduleData));
    }
  }, [moduleData, isTreeTable, collectExpandableKeys]);
  const paginationConfig = {
    current: page,
    pageSize,
    total: Array.isArray(moduleDataResponse)
      ? moduleDataResponse.length
      : (moduleDataResponse?.count ?? 0),
    onChange: (newPage: number, newSize: number) => {
      setPage(newPage);
      setPageSize(newSize ?? 200);
    },
    showTotal: (total: number, range: [number, number]) =>
      `${range[0]}-${range[1]} of ${total}`,
    showSizeChanger: true,
    showQuickJumper: true,
    pageSizeOptions: ["50", "100", "200", "300"],
  };

  const { preferencesData, fetchPreferencesData } = useUser();
  const [columnsState, setColumnsState] = useState<any>({});
  const [size, setSize] = useState<"middle" | "small" | "large">("middle");
  const defaultColumnsState = useMemo(() => {
    const state: Record<string, any> = {};
    columns.forEach(col => {
      if (col.dataIndex) {
        state[col.dataIndex as string] = { show: true }; // default show true
      }
    });
    return state;
  }, [columns]);
  useEffect(() => {
    if (preferencesData?.length > 0) {
      setColumnsState(
        preferencesData?.find((item: any) => item.key === tableName)?.json ?? {}
      );
      setSize(
        preferencesData?.find(
          (item: any) => item.key === gridName.settingsTable
        )?.json ?? "middle"
      );
    }
  }, [preferencesData, tableName]);

  const handleColumnsStateChange = async (newState: any) => {
    setColumnsState(newState);
    if (tableName) {
      const payload = {
        key: tableName,
        json:
          Object.keys(newState).length === 0 ? defaultColumnsState : newState,
      };
      await UserPreferencesService.addUserPreferencesGrid(payload);
      fetchPreferencesData();
    }
  };

  async function handleChangeSize(v: any) {
    setSize(v);
    if (tableName) {
      const payload = {
        key: gridName.settingsTable,
        json: v,
      };
      await UserPreferencesService.addUserPreferencesGrid(payload);
      fetchPreferencesData();
    }
  }

  return (
    <ProTable<T>
      rowKey={rowKey}
      rowSelection={{
        selectedRowKeys,
        columnWidth: 60,
        onChange: newSelectedRowKeys =>
          setSelectedRowKeys?.(newSelectedRowKeys),
        selections: [Table.SELECTION_ALL, Table.SELECTION_INVERT],
      }}
      loading={moduleLoading}
      columns={finalColumns}
      dataSource={moduleData}
      pagination={paginationConfig}
      toolBarRender={toolBarRender}
      indentSize={20}
      columnsState={{
        value: columnsState,
        onChange: handleColumnsStateChange,
      }}
      size={size}
      onSizeChange={(v: any) => {
        handleChangeSize(v);
      }}
      headerTitle={headerTitle}
      request={() => refetch()}
      search={false}
      scroll={{ x: "max-content" }}
      className="max-w-full"
      expandable={
        expandable
          ? {
              expandedRowRender: (record: T) =>
                expandable.expandedRowRender(record),
              rowExpandable: expandable.rowExpandable,
              expandedRowClassName: expandable.expandedRowClassName,
            }
          : isTreeTable
            ? {
                expandedRowKeys,
                onExpandedRowsChange: keys => setExpandedRowKeys([...keys]),
              }
            : undefined
      }
    />
  );
};

export default ConfigDynamicTable;
